import React from "react";
import { createPortal } from "react-dom";
import {
  FiX,
  FiUser,
  FiCalendar,
  FiMapPin,
  FiAlertCircle,
  FiCheckCircle,
  FiFileText,
  FiShield,
} from "react-icons/fi";

export default function BehaviorLogDetailModal({ isOpen, onClose, record }) {
  if (!isOpen || !record) return null;

  // Student name fallback
  const studentName =
    record.student_detail?.full_name ||
    `${record.student_detail?.last_name || ""}, ${record.student_detail?.first_name || ""}`.trim() ||
    (typeof record.student === "object" ? record.student?.name : null) ||
    `Student #${record.student || "N/A"}`;

  // Referring teacher fallback
  const teacherName =
    record.referring_teacher_detail ||
    (typeof record.referring_teacher === "object"
      ? record.referring_teacher?.full_name || record.referring_teacher?.username
      : null) ||
    (record.referring_teacher ? `Teacher #${record.referring_teacher}` : "—");

  // Foreign key text fallbacks
  const schoolYearTerm =
    record.school_year_term_detail ||
    (typeof record.school_year_term === "object" ? record.school_year_term?.name : null) ||
    (record.school_year_term ? `Term #${record.school_year_term}` : "—");

  const classroomName =
    record.classroom_detail ||
    (typeof record.classroom === "object" ? record.classroom?.name : null) ||
    (record.classroom ? `Classroom #${record.classroom}` : "—");

  const subjectAssignmentName =
    record.subject_assignment_detail ||
    (typeof record.subject_assignment === "object" ? record.subject_assignment?.name : null) ||
    (record.subject_assignment ? `Assignment #${record.subject_assignment}` : "—");

  const getInfractionBadge = (level) => {
    switch (level?.toUpperCase()) {
      case "MAJOR":
        return "bg-red-50 text-red-700 border-red-200";
      case "MINOR":
        return "bg-amber-50 text-amber-700 border-amber-200";
      default:
        return "bg-blue-50 text-blue-700 border-blue-200";
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-zinc-900/40 backdrop-blur-xs font-dynamic-title">
      <div className="bg-white rounded-lg border border-zinc-200/80 shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/50">
          <div>
            <h2 className="text-sm font-bold text-zinc-900 flex items-center gap-2">
              Behavior Log Detail
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-zinc-400 hover:text-zinc-600 rounded-lg hover:bg-zinc-100 transition-colors cursor-pointer"
          >
            <FiX className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto space-y-5 text-xs custom-scroll">
          {/* Top Info Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-zinc-50 rounded-lg border border-zinc-200/60">
            <div>
              <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider block mb-1">
                Student
              </span>
              <div className="flex items-center gap-2 font-medium text-zinc-900 text-sm">
                <FiUser className="w-4 h-4 text-zinc-500 shrink-0" />
                <span>{studentName}</span>
              </div>
            </div>

            <div>
              <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider block mb-1">
                Infraction Level & Type
              </span>
              <div className="flex items-center gap-2">
                <span
                  className={`px-2 py-0.5 text-[10px] font-bold rounded-full border ${getInfractionBadge(
                    record.infraction_level
                  )}`}
                >
                  {record.infraction_level || "STANDARD"}
                </span>
                <span className="font-semibold text-zinc-700">
                  {record.violation_type || "N/A"}
                </span>
              </div>
            </div>
          </div>

          {/* Context Details Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 border-b border-zinc-100 pb-4">
            <div>
              <span className="text-zinc-400 block text-[11px]">School Year / Term</span>
              <span className="font-medium text-zinc-800">{schoolYearTerm}</span>
            </div>

            <div>
              <span className="text-zinc-400 block text-[11px]">Classroom</span>
              <span className="font-medium text-zinc-800">{classroomName}</span>
            </div>

            <div>
              <span className="text-zinc-400 block text-[11px]">Subject Assignment</span>
              <span className="font-medium text-zinc-800">{subjectAssignmentName}</span>
            </div>

            <div>
              <span className="text-zinc-400 block text-[11px]">Referring Teacher</span>
              <span className="font-medium text-zinc-800">{teacherName}</span>
            </div>

            <div>
              <span className="text-zinc-400 block text-[11px]">Date & Time</span>
              <div className="flex items-center gap-1 font-medium text-zinc-800 mt-0.5">
                <FiCalendar className="w-3.5 h-3.5 text-zinc-400" />
                <span>{record.date_of_incident || "—"}</span>
                {record.time_of_incident && (
                  <span className="text-zinc-400">({record.time_of_incident})</span>
                )}
              </div>
            </div>

            <div>
              <span className="text-zinc-400 block text-[11px]">Location</span>
              <div className="flex items-center gap-1 font-medium text-zinc-800 mt-0.5">
                <FiMapPin className="w-3.5 h-3.5 text-zinc-400" />
                <span>{record.location || "—"}</span>
              </div>
            </div>
          </div>

          {/* Incident Description */}
          <div className="space-y-1.5">
            <h4 className="font-semibold text-zinc-800 flex items-center gap-1.5">
              <FiAlertCircle className="w-3.5 h-3.5 text-zinc-500" />
              Incident Description
            </h4>
            <div className="p-3 bg-zinc-50/70 rounded-lg border border-zinc-200/60 text-zinc-700 whitespace-pre-wrap leading-relaxed">
              {record.incident_description || "No description provided."}
            </div>
          </div>

          {/* Interventions Section */}
          <div className="space-y-2">
            <h4 className="font-semibold text-zinc-800 flex items-center gap-1.5">
              <FiCheckCircle className="w-3.5 h-3.5 text-zinc-500" />
              Prior Classroom Interventions
            </h4>
            {record.interventions && record.interventions.length > 0 ? (
              <ul className="divide-y divide-zinc-100 border border-zinc-200/60 rounded-lg overflow-hidden">
                {record.interventions.map((item, idx) => (
                  <li key={item.id || idx} className="p-2.5 bg-white flex flex-col gap-1">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="font-semibold text-zinc-800">
                        {item.intervention_type || `Intervention #${idx + 1}`}
                      </span>
                      {item.date_applied && (
                        <span className="text-zinc-400">{item.date_applied}</span>
                      )}
                    </div>
                    {item.notes && (
                      <p className="text-zinc-600 text-[11px]">{item.notes}</p>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-zinc-400 italic text-[11px]">
                No interventions recorded.
              </p>
            )}
          </div>

          {/* VP Notes / Comments */}
          <div className="space-y-1.5">
            <h4 className="font-semibold text-zinc-800 flex items-center gap-1.5">
              <FiShield className="w-3.5 h-3.5 text-zinc-500" />
              VP Notes / Comments
            </h4>
            <div className="p-3 bg-amber-50/40 rounded-lg border border-amber-200/60 text-zinc-700 whitespace-pre-wrap min-h-[50px]">
              {record.vp_notes ? (
                record.vp_notes
              ) : (
                <span className="text-zinc-400 italic">No VP notes added yet.</span>
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-3 border-t border-zinc-100 bg-zinc-50/50 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 text-xs bg-zinc-900 hover:bg-zinc-800 text-white rounded-lg font-medium transition-colors cursor-pointer"
          >
          Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}